import Dexie, { Table } from 'dexie';

import { DATABASE_KEY, OBJECT_STORE_AUTH } from '~/constants';

interface TypeDBLoginInfo {
  id?: number;
  login_id?: string;
  is_remember_id?: boolean;
  access_token?: string | null;
}

interface TypeDBChangePasswordInfo {
  login_id?: string;
}

interface TypeDBTwoFactorAuthInfo {
  login_id?: string;
  two_factor_id?: string | null;
  is_remember_id?: boolean;
}

class AuthIndexedDBDexie extends Dexie {
  loginInfo!: Table<TypeDBLoginInfo>;
  changePasswordInfo!: Table<TypeDBChangePasswordInfo>;
  twoFactorAuthInfo!: Table<TypeDBTwoFactorAuthInfo>;

  constructor() {
    super(DATABASE_KEY.AUTH);

    this.version(OBJECT_STORE_AUTH.LOGIN_INFO.VERSION).stores({
      [OBJECT_STORE_AUTH.LOGIN_INFO.STORE]: OBJECT_STORE_AUTH.LOGIN_INFO.KEY,
    });

    this.version(OBJECT_STORE_AUTH.CHANGE_PASSWORD_INFO.VERSION).stores({
      [OBJECT_STORE_AUTH.CHANGE_PASSWORD_INFO.STORE]: OBJECT_STORE_AUTH.CHANGE_PASSWORD_INFO.KEY,
    });

    this.version(OBJECT_STORE_AUTH.TWO_FACTOR_AUTH_INFO.VERSION).stores({
      [OBJECT_STORE_AUTH.TWO_FACTOR_AUTH_INFO.STORE]: OBJECT_STORE_AUTH.TWO_FACTOR_AUTH_INFO.KEY,
    });
  }

  /**
   * ========== 로그인 ==========
   */
  async getLoginInfo(): Promise<TypeDBLoginInfo | undefined> {
    const loginInfo = await this.loginInfo.toArray();

    return loginInfo?.[0];
  }

  async saveLoginInfo(loginInfo: TypeDBLoginInfo): Promise<void> {
    await this.transaction('rw', this.loginInfo, async () => {
      await this.loginInfo.clear();
      await this.loginInfo.add(loginInfo);
    });
  }

  async logout(): Promise<void> {
    await this.transaction('rw', this.loginInfo, async () => {
      const loginInfo = await this.loginInfo.toArray();

      if (loginInfo.length > 0) {
        const { login_id, is_remember_id } = loginInfo[0];
        await this.loginInfo.clear();

        if (is_remember_id) {
          await this.loginInfo.add({ login_id, is_remember_id });
        }
      }
    });
  }

  /**
   * ========== 비밀번호 변경 ==========
   */
  async getChangePasswordInfo(): Promise<TypeDBChangePasswordInfo | undefined> {
    const changePasswordInfo = await this.changePasswordInfo.toArray();

    return changePasswordInfo?.[0];
  }

  async saveChangePasswordInfo(changePasswordInfo: TypeDBChangePasswordInfo): Promise<void> {
    await this.transaction('rw', this.changePasswordInfo, async () => {
      await this.changePasswordInfo.clear();
      await this.changePasswordInfo.add(changePasswordInfo);
    });
  }

  async removeChangePasswordInfo(): Promise<void> {
    await this.transaction('rw', this.changePasswordInfo, async () => {
      await this.changePasswordInfo.clear();
    });
  }

  /**
   * ========== 2단계 인증 ==========
   */
  async getTwoFactorAuthInfo(): Promise<TypeDBTwoFactorAuthInfo | undefined> {
    const twoFactorAuthInfo = await this.twoFactorAuthInfo.toArray();

    return twoFactorAuthInfo?.[0];
  }

  async saveTwoFactorAuthInfo(twoFactorAuthInfo: TypeDBTwoFactorAuthInfo): Promise<void> {
    await this.transaction('rw', this.twoFactorAuthInfo, async () => {
      await this.twoFactorAuthInfo.clear();
      await this.twoFactorAuthInfo.add(twoFactorAuthInfo);
    });
  }

  async removeTwoFactorAuthInfo(): Promise<void> {
    await this.transaction('rw', this.twoFactorAuthInfo, async () => {
      await this.twoFactorAuthInfo.clear();
    });
  }
}

const authDBService = new AuthIndexedDBDexie();

export default authDBService;
